import { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const links = [
  { label: "The Model", href: "#model" },
  { label: "Projects", href: "#projects" },
  { label: "Segments", href: "#segments" },
  { label: "Why Us", href: "#why" },
  { label: "Reviews", href: "#reviews" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const resolve = (href) => (href.startsWith("#") && pathname !== "/" ? `/${href}` : href);
  const site = useSiteSettings();

  return (
    <motion.header
      initial={{ y: -70 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-border/60 bg-paper/80 backdrop-blur-xl"
      data-testid="main-nav"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
        <a href={resolve("#top")} className="flex items-center gap-3" data-testid="nav-logo">
          <img src="/assets/veera-tripod.png" alt="Urban Veera logo" className="h-9 w-auto" />
          <span className="font-display text-lg font-extrabold uppercase tracking-tight text-navy">
            Urban Veera
            <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber align-middle" aria-hidden="true" />
          </span>
        </a>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-links">
          {links.map((l) => (
            <a
              key={l.href}
              href={resolve(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-spec text-[11px] uppercase tracking-[0.18em] text-steel transition-colors duration-300 hover:text-amberdark"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={resolve("#contact-form")}
            data-testid="nav-call-btn"
            className="group flex items-center gap-2 rounded-full bg-amber px-6 py-3 text-sm font-bold text-white transition-colors duration-300 hover:bg-amberdark"
          >
            Get in Touch
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </a>
        </div>

        <button
          className="text-navy lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-border bg-paper px-6 py-6 lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={resolve(l.href)}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-spec text-xs uppercase tracking-[0.18em] text-navy"
              >
                {l.label}
              </a>
            ))}
            <a
              href={resolve("#contact-form")}
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-cta"
              className="text-sm font-bold text-amberdark"
            >
              Get a Project Feasibility →
            </a>
            <a href={`tel:${site.phone.replace(/\s/g, "")}`} data-testid="nav-mobile-call-btn" className="text-sm font-bold text-navy">
              Call {site.phone}
            </a>
          </div>
        </div>
      )}
    </motion.header>
  );
};
